import { BackendTopic, ThemeTopic, ProgressReportDataType, RootData } from "src/interfaces/ProgressReport";

export function dateDiffInDays(a: Date, b: Date) {
  const _MS_PER_DAY = 1000 * 60 * 60 * 24;
  // Discard the time and time-zone information.
  const utc1 = Date.UTC(a.getFullYear(), a.getMonth(), a.getDate());
  const utc2 = Date.UTC(b.getFullYear(), b.getMonth(), b.getDate());

  let diff = Math.floor((utc2 - utc1) / _MS_PER_DAY);

  if (diff > 0) {
    return diff;
  }
  return 0;
}

export function isValidJSON(str: string): boolean {
  try {
    const response = JSON.parse(str);
    return (
      response &&
      typeof response === 'object' &&
      'ask_user_question_mode' in response
    );
  } catch (e) {
    return false;
  }
}

export function isAskModeJSON(str: string): boolean {
  try {
    const obj = JSON.parse(str);
    return obj && typeof obj === 'object' && !!obj.ask_user_question_mode;
  } catch {
    return false;
  }
}

export function isImageMessageJSON(str: string): boolean {
  try {
    const obj = JSON.parse(str);
    return (
      obj &&
      typeof obj === 'object' &&
      typeof obj.text === 'string' &&
      typeof obj.image_url === 'string'
    );
  } catch {
    return false;
  }
}

export const formatDate = (dateString: string) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
};

// export function transformToProgressReport(
//   data: RootData
// ): ProgressReportDataType {
//   const {
//     aggregated_subject_result,
//     scores: { themes },
//   } = data;

//   const transformedThemes: Record<string, ThemeTopic[]> = {};

//   for (const [themeName, themeObj] of Object.entries<any>(themes)) {
//     const topicsArray: ThemeTopic[] = [];

//     for (const [topicName, topicObj] of Object.entries<any>(
//       themeObj.topics || {}
//     )) {
//       topicsArray.push({
//         topic: topicName,
//         metrics: {
//           accuracyRate:
//             topicObj.accuracy !== null && topicObj.accuracy !== undefined
//               ? Number(topicObj.accuracy) * 100
//               : NOT_AVAILABLE_PLACE_HOLDER,
//           attempts:
//             topicObj.total_attempts !== null &&
//             topicObj.total_attempts !== undefined
//               ? topicObj.total_attempts
//               : NOT_AVAILABLE_PLACE_HOLDER,
//           misconceptions: topicObj.misconceptions?.length
//             ? topicObj.misconceptions
//             : 0,
//           bloomLevel: topicObj.bloom_level
//             ? topicObj.bloom_level
//             : NOT_AVAILABLE_PLACE_HOLDER,
//           hintsRequested:
//             topicObj.total_hints !== null && topicObj.total_hints !== undefined
//               ? topicObj.total_hints
//               : NOT_AVAILABLE_PLACE_HOLDER,
//         },
//       });
//     }

//     transformedThemes[themeName] = topicsArray;
//   }

//   // Map the aggregated_subject_result fields to ProgressReportDataType fields
//   return {
//     totalQuestionsAttempted:
//       aggregated_subject_result.total_attempts !== null &&
//       aggregated_subject_result.total_attempts !== undefined
//         ? aggregated_subject_result.total_attempts
//         : NOT_AVAILABLE_PLACE_HOLDER,
//     correctVsIncorrect: `${
//       aggregated_subject_result.success_attempts !== null &&
//       aggregated_subject_result.success_attempts !== undefined
//         ? aggregated_subject_result.success_attempts
//         : NOT_AVAILABLE_PLACE_HOLDER
//     } vs ${
//       aggregated_subject_result.failed_attempts !== null &&
//       aggregated_subject_result.failed_attempts !== undefined
//         ? aggregated_subject_result.failed_attempts
//         : NOT_AVAILABLE_PLACE_HOLDER
//     }`,
//     improvementOnRetried:
//       aggregated_subject_result.self_corrected !== null &&
//       aggregated_subject_result.self_corrected !== undefined
//         ? aggregated_subject_result.self_corrected
//           ? 'Yes'
//           : 'No'
//         : NOT_AVAILABLE_PLACE_HOLDER,
//     topPerformingTopics:
//       aggregated_subject_result.top_performing_topics &&
//       aggregated_subject_result.top_performing_topics.length > 0
//         ? aggregated_subject_result.top_performing_topics
//         : NOT_AVAILABLE_PLACE_HOLDER,
//     challengingTopics:
//       aggregated_subject_result.challenging_topics &&
//       aggregated_subject_result.challenging_topics.length > 0
//         ? aggregated_subject_result.challenging_topics
//         : NOT_AVAILABLE_PLACE_HOLDER,
//     weeklyMonthlyTrends: NOT_AVAILABLE_PLACE_HOLDER, // Placeholder
//     progressionOfMasteryLevels:
//       aggregated_subject_result.bloom_level ?? NOT_AVAILABLE_PLACE_HOLDER,
//     milestonesAchieved: NOT_AVAILABLE_PLACE_HOLDER, // Placeholder
//     recommendedFocusAreas:
//       aggregated_subject_result.focus_areas &&
//       aggregated_subject_result.focus_areas.length > 0
//         ? aggregated_subject_result.focus_areas
//         : NOT_AVAILABLE_PLACE_HOLDER,
//     themes: transformedThemes,
//   };
// }

// export const NOT_AVAILABLE_PLACE_HOLDER = 'N/A';
// Updated transformToProgressReport and types to match new backend shape

export const NOT_AVAILABLE_PLACE_HOLDER = 'N/A';

function topicToFrontend(topicName: string, topicObj: BackendTopic) : ThemeTopic {
  const acc = topicObj && topicObj.accuracy !== null && topicObj.accuracy !== undefined
    ? Number(topicObj.accuracy) * 100
    : NOT_AVAILABLE_PLACE_HOLDER;

  const attemptsNum = topicObj && topicObj.total_attempts !== null && topicObj.total_attempts !== undefined
    ? Number(topicObj.total_attempts) || 0
    : 0;

  const attempts = attemptsNum;

  const misconceptions = topicObj && topicObj.misconceptions && topicObj.misconceptions.length
    ? topicObj.misconceptions
    : 0;

  const bloomLevel = topicObj && (topicObj.bloom_level ?? topicObj.bloomLevel) ? (topicObj.bloom_level ?? topicObj.bloomLevel) : NOT_AVAILABLE_PLACE_HOLDER;

  const hintsRequested = topicObj && topicObj.total_hints !== null && topicObj.total_hints !== undefined
    ? topicObj.total_hints
    : NOT_AVAILABLE_PLACE_HOLDER;

  const score = topicObj && topicObj.score !== null && topicObj.score !== undefined
    ? topicObj.score
    : NOT_AVAILABLE_PLACE_HOLDER;

  // Read success_attempts and failed_attempts directly from backend
  let correctCount: number | undefined = undefined;
  let incorrectCount: number | undefined = undefined;

  if (topicObj && topicObj.success_attempts !== null && topicObj.success_attempts !== undefined) {
    correctCount = Number(topicObj.success_attempts);
  }

  if (topicObj && topicObj.failed_attempts !== null && topicObj.failed_attempts !== undefined) {
    incorrectCount = Number(topicObj.failed_attempts);
  }

  // Fallback: compute correct / incorrect counts from accuracy if backend doesn't provide them
  if (correctCount === undefined && incorrectCount === undefined && typeof acc === 'number' && attemptsNum > 0) {
    correctCount = Math.round((acc / 100) * attemptsNum);
    incorrectCount = attemptsNum - correctCount;
  }

  return {
    topic: topicName,
    metrics: {
      accuracyRate: acc,
      attempts: attempts,
      misconceptions: misconceptions,
      bloomLevel: bloomLevel,
      hintsRequested: hintsRequested,
      score: score,
      total: attemptsNum,
      correct: correctCount,
      incorrect: incorrectCount,
    }
  };
}

export function transformToProgressReport(data: RootData): ProgressReportDataType {
  const { aggregated_subject_result, scores } = data || {};
  const transformedLevels: Record<string, ThemeTopic[]> = {};

  // Prefer new shape: scores.levels
  if (scores && scores.levels) {
    for (const [levelName, levelObj] of Object.entries(scores.levels)) {
      const topicsArray: ThemeTopic[] = [];
      const topicsMap = (levelObj && (levelObj.topics || {})) || {};
      for (const [topicName, topicObj] of Object.entries<BackendTopic>(topicsMap)) {
        const t = topicToFrontend(topicName, topicObj);
        t.level = levelName;
        topicsArray.push(t);
      }
      transformedLevels[levelName] = topicsArray;
    }

    // If multiple levels exist, add an 'All' pseudo-level containing all topics
    const levelKeys = Object.keys(transformedLevels);
    if (levelKeys.length > 1) {
      const allTopics: ThemeTopic[] = [];
      for (const lk of levelKeys) {
        const arr = transformedLevels[lk] || [];
        for (const topicEntry of arr) {
          // push a shallow copy so later changes won't mutate originals
          allTopics.push({ ...topicEntry });
        }
      }
      transformedLevels['All'] = allTopics;
    }
  } else if (scores && scores.themes) {
    // Legacy path: convert themes -> single level keyed by a default name
    const levelKey = 'default';
    transformedLevels[levelKey] = [];
    for (const [themeName, themeObj] of Object.entries<any>(scores.themes)) {
      const topicsMap = (themeObj && themeObj.topics) ? themeObj.topics : {};
      for (const [topicName, topicObj] of Object.entries<BackendTopic>(topicsMap)) {
          const t = topicToFrontend(topicName, topicObj);
          t.level = levelKey;
          transformedLevels[levelKey].push(t);
      }
    }
  }

  const agg = aggregated_subject_result || {};

  const totalQuestionsAttempted =
    agg.total_attempts !== null && agg.total_attempts !== undefined ? agg.total_attempts : NOT_AVAILABLE_PLACE_HOLDER;

  const correctVsIncorrect = `${agg.success_attempts !== null && agg.success_attempts !== undefined ? agg.success_attempts : NOT_AVAILABLE_PLACE_HOLDER} vs ${agg.failed_attempts !== null && agg.failed_attempts !== undefined ? agg.failed_attempts : NOT_AVAILABLE_PLACE_HOLDER}`;

  const improvementOnRetried =
    agg.self_corrected !== null && agg.self_corrected !== undefined
      ? (agg.self_corrected ? 'Yes' : 'No')
      : NOT_AVAILABLE_PLACE_HOLDER;

  const topPerformingTopics = agg.top_performing_topics && agg.top_performing_topics.length > 0 ? agg.top_performing_topics : NOT_AVAILABLE_PLACE_HOLDER;
  const challengingTopics = agg.challenging_topics && agg.challenging_topics.length > 0 ? agg.challenging_topics : NOT_AVAILABLE_PLACE_HOLDER;
  const progressionOfMasteryLevels = agg.bloom_level ?? NOT_AVAILABLE_PLACE_HOLDER;
  const recommendedFocusAreas = agg.focus_areas && agg.focus_areas.length > 0 ? agg.focus_areas : NOT_AVAILABLE_PLACE_HOLDER;

  return {
    levels: transformedLevels,
    totalQuestionsAttempted,
    correctVsIncorrect,
    improvementOnRetried,
    topPerformingTopics,
    challengingTopics,
    weeklyMonthlyTrends: NOT_AVAILABLE_PLACE_HOLDER,
    progressionOfMasteryLevels,
    milestonesAchieved: NOT_AVAILABLE_PLACE_HOLDER,
    recommendedFocusAreas,
    themes: {},
  };
}

